import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.tsx';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';
import './i18n'; // Initialize i18n before app starts
import { hasAnalyticsConsent } from './utils/cookieConsent';

// Lazy load analytics and heavy styles
let analyticsInitialized = false;

const initializeAnalytics = async () => {
  if (analyticsInitialized || !hasAnalyticsConsent()) {
    return;
  }

  const { initGA } = await import('./utils/analytics');
  initGA();
  analyticsInitialized = true;
};

const disableAnalytics = () => {
  // Tell GA to stop using analytics storage for future events sent
  // from this session (Consent Mode v2-style signal).
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      });
    }
  } catch {
    // ignore
  }
  analyticsInitialized = false;
};

// Lazy load Quill styles only when needed
export const loadQuillStyles = () => {
  import('react-quill/dist/quill.snow.css');
};

// Initialize Google Analytics asynchronously (respects stored consent)
initializeAnalytics();

if (typeof window !== 'undefined') {
  window.addEventListener('cookieConsentUpdated', (event) => {
    const detail = (event as CustomEvent<{ analytics?: boolean }>).detail;
    if (detail?.analytics) {
      initializeAnalytics();
    } else {
      disableAnalytics();
    }
  });
}

// Initialize Core Web Vitals monitoring
const initWebVitals = async () => {
  const { initializeWebVitals } = await import('./utils/coreWebVitals');
  initializeWebVitals();
};

initWebVitals();

// Load GTM testing utilities for debugging
const loadGTMTestUtils = async () => {
  try {
    await import('./utils/manualGTMTest');
    console.log('🛠️ GTM test utilities loaded for debugging');
    
    // Load chat debug utilities in development
    if (import.meta.env.DEV) {
      const { debugChatSystem } = await import('./utils/chatDebug');
      const { testChatSystem } = await import('./utils/chatSystemTest');
      const { debugCurrentConversation } = await import('./utils/chatBackendDebug');
      
      (window as typeof window & { 
        debugChatSystem: typeof debugChatSystem;
        testChatSystem: typeof testChatSystem;
        debugCurrentConversation: typeof debugCurrentConversation;
      }).debugChatSystem = debugChatSystem;
      (window as typeof window & { 
        debugChatSystem: typeof debugChatSystem;
        testChatSystem: typeof testChatSystem;
        debugCurrentConversation: typeof debugCurrentConversation;
      }).testChatSystem = testChatSystem;
      (window as typeof window & { 
        debugChatSystem: typeof debugChatSystem;
        testChatSystem: typeof testChatSystem;
        debugCurrentConversation: typeof debugCurrentConversation;
      }).debugCurrentConversation = debugCurrentConversation;
      
      console.log('🛠️ Chat debug utilities loaded');
      console.log('   - Use debugChatSystem() for detailed debug');
      console.log('   - Use testChatSystem() for comprehensive testing');
      console.log('   - Use debugCurrentConversation() to fix the failing conversation');
    }
  } catch (error) {
    console.error('❌ Failed to load debug utilities:', error);
  }
};

loadGTMTestUtils();

// Performance optimizations removed - caused React initialization blocking

// Detect Safari browser
const isSafari = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  return userAgent.includes('safari') && !userAgent.includes('chrome') && !userAgent.includes('chromium');
};

// Detect iOS devices
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || 
         (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

// Service Worker Debug Utilities
const debugServiceWorker = () => {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      console.log('🔍 Active Service Workers:', registrations.length);
      registrations.forEach((registration, index) => {
        console.log(`SW ${index + 1}:`, {
          scope: registration.scope,
          state: registration.active?.state,
          scriptURL: registration.active?.scriptURL
        });
      });
    });
  }
};

// Register Firebase Messaging Service Worker with Safari compatibility
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.warn('⚠️ Service Workers not supported in this browser');
    return null;
  }

  const browserInfo = {
    isSafari: isSafari(),
    isIOS: isIOS(),
    userAgent: navigator.userAgent
  };
  
  console.log('🔍 Browser detection:', browserInfo);

  try {
    // Safari-specific handling
    if (browserInfo.isSafari || browserInfo.isIOS) {
      console.log('🍎 Safari/iOS detected - using enhanced registration');
      
      // Clear any corrupted service worker cache for Safari
      const existingRegistrations = await navigator.serviceWorker.getRegistrations();
      if (existingRegistrations.length > 0) {
        console.log('🔄 Found existing service workers, checking health...');
        debugServiceWorker();
      }
    }

    console.log('🔄 Registering Firebase Messaging Service Worker...');
    
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
      updateViaCache: 'none' // Prevent aggressive caching issues in Safari
    });
    
    console.log('✅ Firebase Messaging Service Worker registered:', registration);
    
    // Register additional caching service worker for performance
    try {
      const cacheRegistration = await navigator.serviceWorker.register('/sw-cache.js', {
        scope: '/',
        updateViaCache: 'none'
      });
      console.log('✅ Caching Service Worker registered:', cacheRegistration);
    } catch (cacheError) {
      console.warn('⚠️ Caching Service Worker registration failed:', cacheError);
    }
    
    // Enhanced update handling for Safari
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (newWorker) {
        console.log('🔄 Service Worker update found');
        newWorker.addEventListener('statechange', () => {
          console.log('🔄 Service Worker state changed:', newWorker.state);
          if (newWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              console.log('🔄 New Firebase Messaging Service Worker available');
              // For Safari, we might want to reload to prevent conflicts
              if (browserInfo.isSafari) {
                console.log('🍎 Safari: Service Worker updated, reload recommended');
              }
            } else {
              console.log('✅ Service Worker installed for the first time');
            }
          }
        });
      }
    });
    
    // Safari-specific error monitoring
    if (browserInfo.isSafari || browserInfo.isIOS) {
      navigator.serviceWorker.addEventListener('error', (event) => {
        console.error('🍎 Safari Service Worker Error:', event);
        console.log('💡 Try clearing Safari data: Settings → Safari → Clear History and Website Data');
      });
    }
    
    return registration;
  } catch (error) {
    console.error('❌ Firebase Messaging Service Worker registration failed:', error);
    
    // Safari-specific error handling
    if (browserInfo.isSafari || browserInfo.isIOS) {
      console.log('🍎 Safari Service Worker Issue Detected');
      console.log('💡 Troubleshooting steps:');
      console.log('   1. Clear Safari cache: Settings → Safari → Clear History and Website Data');
      console.log('   2. Force close Safari and reopen');
      console.log('   3. Try in Private Browsing mode to confirm the issue');
      console.log('   4. Check if the site works on other devices');
      
      // Attempt to unregister corrupted service workers
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        if (registrations.length > 0) {
          console.log('🔄 Attempting to clean up corrupted service workers...');
          for (const registration of registrations) {
            await registration.unregister();
            console.log('🗑️ Unregistered service worker:', registration.scope);
          }
          console.log('✅ Service worker cleanup complete. Reload the page to try again.');
        }
      } catch (cleanupError) {
        console.error('❌ Service worker cleanup failed:', cleanupError);
      }
    }
    
    return null;
  }
};

// Global utilities for debugging (accessible from browser console)
// Type declaration for global scope
declare global {
  interface Window {
    debugConnectAndGo: () => void;
    clearServiceWorkers: () => Promise<void>;
    clearAllCache: () => void;
    fixChunkLoadingError: () => Promise<void>;
    testFirebaseImages: () => Promise<void>;
    fixSafariIssues: () => Promise<void>;
    debugFirebase: () => Promise<void>;
    testNotifications: () => Promise<void>;
    testGA4: () => void;
    forceGA4PageView: (title?: string) => void;
    printFirebaseIdToken: () => Promise<void>;
    clearFirebaseTokenCache: () => void;
    checkTokenStatus: () => void;
    forceTokenRefresh: () => Promise<void>;
    emergencyResetFirebase: () => void;
    // PageSpeed optimization utilities (disabled)
  }
}

// Debug utility - call from console: debugConnectAndGo()
window.debugConnectAndGo = () => {
  console.log('🔍 ConnectAndGo Debug Information:');
  console.log('Browser:', {
    userAgent: navigator.userAgent,
    isSafari: isSafari(),
    isIOS: isIOS(),
    serviceWorkerSupport: 'serviceWorker' in navigator
  });
  
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(registrations => {
      console.log('🔧 Service Workers:', registrations.length);
      registrations.forEach((reg, i) => {
        console.log(`SW ${i + 1}:`, {
          scope: reg.scope,
          state: reg.active?.state,
          scriptURL: reg.active?.scriptURL
        });
      });
    });
  }
  
  console.log('📱 Storage:');
  console.log('  localStorage items:', localStorage.length);
  console.log('  sessionStorage items:', sessionStorage.length);
  
  console.log('🍪 Cookies:', document.cookie ? 'Present' : 'None');
};

// Clear all service workers - call from console: clearServiceWorkers()
window.clearServiceWorkers = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('❌ Service Workers not supported');
    return;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    console.log(`🔄 Found ${registrations.length} service worker(s) to remove`);
    
    for (const registration of registrations) {
      await registration.unregister();
      console.log('🗑️ Unregistered:', registration.scope);
    }
    
    console.log('✅ All service workers cleared');
    console.log('💡 Reload the page to continue normally');
  } catch (error) {
    console.error('❌ Failed to clear service workers:', error);
  }
};

// Clear all cached data (including stale activities) - call from console: clearAllCache()
window.clearAllCache = () => {
  try {
    console.log('🧹 Clearing all cached data...');
    
    // Clear all session storage
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      sessionStorage.removeItem(key);
      console.log('🗑️ Removed session key:', key);
    });
    
    // Clear specific local storage cache keys
    const cacheKeys = [
      'public_home_cache_v1',
      'public_home_cache_v2',
      'activities_cache',
      'categories_cache',
      'cities_cache'
    ];
    
    cacheKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log('🗑️ Removed local cache key:', key);
      }
    });
    
    // Clear Service Worker cache to fix chunk loading errors
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          if (registration.active && registration.active.scriptURL.includes('sw-cache.js')) {
            console.log('🔄 Clearing Service Worker cache to fix chunk loading errors');
            const messageChannel = new MessageChannel();
            messageChannel.port1.onmessage = (event) => {
              if (event.data.success) {
                console.log('✅ Service Worker cache cleared');
                console.log('🔄 Refreshing page to reload fresh data...');
                window.location.reload();
              }
            };
            registration.active.postMessage(
              { type: 'CLEAR_CACHE' },
              [messageChannel.port2]
            );
            return; // Exit early if we found the cache SW
          }
        });
        
        // If no cache SW found, just reload
        console.log('ℹ️ No cache service worker found, proceeding with reload');
        console.log('✅ All cached data cleared');
        console.log('🔄 Refreshing page to reload fresh data...');
        window.location.reload();
      });
    } else {
      console.log('✅ All cached data cleared');
      console.log('🔄 Refreshing page to reload fresh data...');
      window.location.reload();
    }
  } catch (error) {
    console.error('❌ Error clearing cache:', error);
  }
};

// Fix chunk loading errors specifically - call from console: fixChunkLoadingError()
window.fixChunkLoadingError = async () => {
  console.log('🔧 Fixing chunk loading errors...');
  
  try {
    // Clear Service Worker cache first
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        if (registration.active && registration.active.scriptURL.includes('sw-cache.js')) {
          console.log('🔄 Clearing Service Worker cache...');
          const messageChannel = new MessageChannel();
          
          await new Promise((resolve) => {
            messageChannel.port1.onmessage = (event) => {
              if (event.data.success) {
                console.log('✅ Service Worker cache cleared');
              }
              resolve(event.data);
            };
            registration.active!.postMessage(
              { type: 'CLEAR_CACHE' },
              [messageChannel.port2]
            );
          });
          break;
        }
      }
    }
    
    // Clear browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map(cacheName => {
          console.log('🗑️ Deleting cache:', cacheName);
          return caches.delete(cacheName);
        })
      );
    }
    
    // Clear localStorage items that might interfere
    const problematicKeys = ['public_home_cache_v1', 'public_home_cache_v2'];
    problematicKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        console.log('🗑️ Removed localStorage key:', key);
      }
    });
    
    console.log('✅ Chunk loading error fix complete!');
    console.log('🔄 Please reload the page manually to test the fix');
    console.log('💡 If the error persists, try opening in an incognito/private window');
    
  } catch (error) {
    console.error('❌ Failed to fix chunk loading error:', error);
    console.log('💡 Manual fix: Clear browser cache and reload');
  }
};

// Test Firebase Storage image loading - call from console: testFirebaseImages()
window.testFirebaseImages = async () => {
  console.log('🔥 Testing Firebase Storage image loading...');
  
  // Test a sample Firebase Storage image URL
  const testImageUrl = 'https://firebasestorage.googleapis.com/v0/b/connectandgo-f556f.firebasestorage.app/o/activity_images%2Ftest.jpg?alt=media';
  
  try {
    const response = await fetch(testImageUrl, { method: 'HEAD' });
    console.log('📡 Firebase Storage Response:', {
      status: response.status,
      statusText: response.statusText,
      headers: {
        'access-control-allow-origin': response.headers.get('access-control-allow-origin'),
        'cache-control': response.headers.get('cache-control'),
        'content-type': response.headers.get('content-type'),
      }
    });
    
    if (response.ok) {
      console.log('✅ Firebase Storage is accessible');
    } else {
      console.log('⚠️ Firebase Storage responded with error:', response.status);
    }
  } catch (error) {
    console.error('❌ Firebase Storage test failed:', error);
    console.log('💡 This might indicate CORS or network issues');
  }
  
  // Test image loading via Image object
  console.log('🖼️ Testing image loading via Image object...');
  const img = new Image();
  img.crossOrigin = 'anonymous';
  
  img.onload = () => {
    console.log('✅ Image loaded successfully via Image object');
  };
  
  img.onerror = (error) => {
    console.error('❌ Image failed to load via Image object:', error);
  };
  
  img.src = testImageUrl;
};

// Safari-specific fix utility - call from console: fixSafariIssues()
window.fixSafariIssues = async () => {
  console.log('🍎 Safari Issues Fix Utility');
  
  if (!isSafari() && !isIOS()) {
    console.log('ℹ️ This doesn\'t appear to be Safari/iOS, but running anyway...');
  }
  
  try {
    // Step 1: Clear service workers
    console.log('Step 1: Clearing service workers...');
    await window.clearServiceWorkers();
    
    // Step 2: Clear localStorage
    console.log('Step 2: Clearing localStorage...');
    const localStorageCount = localStorage.length;
    localStorage.clear();
    console.log(`🗑️ Cleared ${localStorageCount} localStorage items`);
    
    // Step 3: Clear sessionStorage
    console.log('Step 3: Clearing sessionStorage...');
    const sessionStorageCount = sessionStorage.length;
    sessionStorage.clear();
    console.log(`🗑️ Cleared ${sessionStorageCount} sessionStorage items`);
    
    console.log('✅ Safari fix complete!');
    console.log('💡 Next steps:');
    console.log('  1. Reload this page');
    console.log('  2. If still broken, clear Safari data: Settings → Safari → Clear History and Website Data');
    console.log('  3. Force close Safari and reopen');
    
  } catch (error) {
    console.error('❌ Safari fix failed:', error);
    console.log('💡 Manual fix: Clear Safari data in iPhone Settings');
  }
};

// Firebase debugging utility - call from console: debugFirebase()
window.debugFirebase = async () => {
  console.log('🔥 Firebase Connection Debug');
  
  try {
    // Import Firebase modules for testing
    const { getFCMToken, messaging, auth } = await import('./config/firebase');
    
    console.log('📋 Firebase Modules Status:', {
      messagingExists: !!messaging,
      authExists: !!auth,
      getFCMTokenExists: !!getFCMToken
    });
    
    // Check Firebase Auth
    if (auth) {
      console.log('🔐 Firebase Auth:', {
        currentUser: !!auth.currentUser,
        userEmail: auth.currentUser?.email || 'Not logged in',
        appName: auth.app.name,
        projectId: auth.app.options.projectId
      });

      // Also print an ID token for backend debugging
      if (auth.currentUser) {
        try {
          const idToken = await auth.currentUser.getIdToken(true);
          console.log('🔑 Firebase ID Token (Authorization: Bearer ...):');
          console.log(idToken);
          try {
            await navigator.clipboard.writeText(idToken);
            console.log('📋 Token copied to clipboard');
          } catch {
            console.log('ℹ️ Clipboard copy not available');
          }
        } catch (e) {
          console.error('❌ Failed to get Firebase ID token:', e);
        }
      }
    }
    
    // Check service worker registration
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      console.log('🛠️ Service Worker Status:', {
        registrations: registrations.length,
        firebaseMessagingSW: registrations.find(reg => 
          reg.active?.scriptURL.includes('firebase-messaging-sw.js')
        ) ? 'Found' : 'Not found'
      });
      
      registrations.forEach((reg, i) => {
        console.log(`SW ${i + 1}:`, {
          scope: reg.scope,
          state: reg.active?.state,
          scriptURL: reg.active?.scriptURL.split('/').pop()
        });
      });
    }
    
    // Check notification permission
    const permission = Notification.permission;
    console.log('🔔 Notification Permission:', permission);
    
    // Check environment variables
    console.log('🔍 Environment Variables:', {
      hasFirebaseApiKey: !!import.meta.env.VITE_FIREBASE_API_KEY,
      hasMessagingSenderId: !!import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      hasAppId: !!import.meta.env.VITE_FIREBASE_APP_ID,
      hasAnalyticsId: !!import.meta.env.VITE_GOOGLE_ANALYTICS_ID,
      apiBaseUrl: import.meta.env.VITE_API_BASE_URL || 'Not set'
    });
    
    // Try to get FCM token
    console.log('🔄 Testing FCM Token Generation...');
    const token = await getFCMToken();
    
    if (token) {
      console.log('✅ FCM Token Test: SUCCESS');
      console.log('📱 Token preview:', token.substring(0, 30) + '...');
    } else {
      console.log('❌ FCM Token Test: FAILED');
    }
    
  } catch (error) {
    console.error('❌ Firebase debug failed:', error);
  }
};

// Quick helper to print/copy the current Firebase ID token
// Usage: printFirebaseIdToken()
window.printFirebaseIdToken = async () => {
  try {
    const { auth } = await import('./config/firebase');
    if (!auth.currentUser) {
      console.log('⚠️ No authenticated user');
      return;
    }
    const idToken = await auth.currentUser.getIdToken(true);
    console.log('🔑 Firebase ID Token:');
    console.log(idToken);
    try {
      await navigator.clipboard.writeText(idToken);
      console.log('📋 Token copied to clipboard');
    } catch {
      console.log('ℹ️ Clipboard copy not available');
    }
  } catch (e) {
    console.error('❌ Failed to print Firebase ID token:', e);
  }
};

// Clear Firebase token cache utility
window.clearFirebaseTokenCache = async () => {
  try {
    const { clearTokenCache } = await import('./services/api');
    clearTokenCache();
    console.log('✅ Firebase token cache cleared');
  } catch (e) {
    console.error('❌ Failed to clear token cache:', e);
  }
};

// Check Firebase token status
window.checkTokenStatus = async () => {
  try {
    const { getTokenStatus } = await import('./services/api');
    const status = getTokenStatus();
    console.log('🔍 Firebase Token Status:', {
      ...status,
      tokenExpiryTime: status.tokenExpiry ? new Date(status.tokenExpiry).toLocaleString() : 'No token',
      backoffUntilTime: status.backoffUntil ? new Date(status.backoffUntil).toLocaleString() : 'No backoff',
      minutesUntilBackoffEnd: status.isInBackoff ? 
        Math.ceil((status.backoffUntil - Date.now()) / 60000) : 0
    });
  } catch (e) {
    console.error('❌ Failed to check token status:', e);
  }
};

// Force refresh Firebase token
window.forceTokenRefresh = async () => {
  try {
    console.log('🔄 Forcing Firebase token refresh...');
    const { forceTokenRefresh } = await import('./services/api');
    await forceTokenRefresh();
    console.log('✅ Token refresh successful');
  } catch (e) {
    console.error('❌ Failed to force token refresh:', e);
  }
};

// Emergency reset function for Firebase quota issues
window.emergencyResetFirebase = async () => {
  try {
    console.log('🚨 Emergency Firebase reset initiated...');
    
    const { clearTokenCache, getTokenStatus } = await import('./services/api');
    
    console.log('Before reset:', getTokenStatus());
    clearTokenCache();
    console.log('After reset:', getTokenStatus());
    
    console.log('✅ Emergency reset complete - all Firebase state cleared');
    console.log('💡 Tip: Refresh the page and try again');
  } catch (e) {
    console.error('❌ Failed to reset Firebase state:', e);
  }
};

// Test notifications utility - call from console: testNotifications()
window.testNotifications = async () => {
  console.log('🧪 Testing Notification System...');
  
  try {
    // Test notification permission
    const permission = await Notification.requestPermission();
    console.log('🔔 Permission result:', permission);
    
    if (permission === 'granted') {
      // Test browser notification
      const notification = new Notification('🧪 ConnectAndGo Test', {
        body: 'If you see this, browser notifications work!',
        icon: '/treffpunkslogo.svg',
        badge: '/treffpunkslogo.svg'
      });
      
      notification.onclick = () => {
        console.log('✅ Notification clicked');
        notification.close();
      };
      
      setTimeout(() => notification.close(), 5000);
      console.log('✅ Test notification sent');
    } else {
      console.log('❌ Notification permission denied');
    }
    
    // Test FCM
    const { getFCMToken } = await import('./config/firebase');
    const token = await getFCMToken();
    
    if (token) {
      console.log('✅ FCM setup working');
      console.log('📱 Ready to receive push notifications');
    } else {
      console.log('❌ FCM setup failed');
    }
    
  } catch (error) {
    console.error('❌ Notification test failed:', error);
  }
};

// GA4 Testing Functions
window.testGA4 = async () => {
  console.log('🔧 Testing Google Analytics 4 Connection...');
  const { testGA4Connection } = await import('./utils/analytics');
  testGA4Connection();
};

window.forceGA4PageView = async (title = 'Console Test Page') => {
  console.log('🧪 Forcing GA4 Page View...');
  const { forcePageView } = await import('./utils/analytics');
  forcePageView(title);
};

// PageSpeed Optimization Functions
// PageSpeed test function removed

// PageSpeed debug functions removed - performance optimizations disabled

// Initialize app with minimal setup
const initializeApp = async () => {
  console.log('🚀 Starting React app initialization...');
  
  // Render the React app FIRST (without waiting for service worker)
  const rootElement = document.getElementById('root');
  if (rootElement) {
    console.log('✅ Root element found, mounting React...');
    ReactDOM.createRoot(rootElement).render(
      <React.StrictMode>
        <HelmetProvider>
          <App />
        </HelmetProvider>
      </React.StrictMode>,
    );
    console.log('✅ React app mounted successfully');
  } else {
    console.error('❌ Root element not found');
    return;
  }
  
  // Register service worker AFTER React is mounted (non-blocking)
  setTimeout(() => {
    registerServiceWorker().catch(error => {
      console.warn('⚠️ Service worker registration failed:', error);
    });
  }, 1000);
};

// Start the application
initializeApp(); 
